import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  IAttendanceFlowModalConfig,
  IAttendanceOriginValuePair,
  IPostAssistanceRegister,
  IPostDuplicateAttendanceBody,
  IPostDuplicateAttendanceResponse,
} from "src/app/attendanceRecord/interfaces/AssistanceOpcionalInterface";
import { environment } from "src/environments/environment";
import { ICollaborator } from "src/app/models/Interfaces/Collaborators/CollaboratorsResponseInterface";
import { CollaboratorService } from "src/app/services/Collaborators/collaborators.service";
import { ResponseDataInterfaces } from "src/app/models/Interfaces/ResponseDataInterfaces";
import { originDataEnumEmailPerAd } from "../../constants/idOriginAsistance";
import {
  IAttendant,
  IDuplicateAttendanceSession,
  IGetDuplicatesDetailsParams,
  IGetDuplicatesParams,
} from "src/app/reports/interfaces/reports.interfaces";
import { ModalSharedServiceService } from "src/app/shared/modals/modal-service.service";
import { UtilsAssistanceService } from "../utils/utiils.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ModalValidacionComponent } from "../../components/modal-validacion/modal-validacion.component";
import {
  errorDuplicateAttendant,
  unknownErrorOnAttendance,
} from "../../constants/attendanceRegisterErrorMessages";

@Injectable({
  providedIn: "root",
})
export class AssistanceService {
  private endPoint = `${environment.apiUrlCap}/api/assistance`;
  constructor(
    private http: HttpClient,
    private collaboratorsService: CollaboratorService,
    private sharedModal: ModalSharedServiceService,
    private utils: UtilsAssistanceService,
    private modalService: NgbModal
  ) {}

  async getUserInfoHCM(
    origin: originDataEnumEmailPerAd,
    payload: string
  ): Promise<ICollaborator | null> {
    let userData: ICollaborator | null = null;
    let encodedEmail: string;
    let generalError: any;
    switch (origin) {
      case originDataEnumEmailPerAd.activeDirectory:
        encodedEmail = encodeURIComponent(payload);
        await new Promise<void>(resolve => {
          this.collaboratorsService.GetByEmail(encodedEmail).subscribe({
            next: d => {
              const t = d.data as ICollaborator;
              if (t) {
                userData = t;
                if (!t.email) {
                  userData.email = payload;
                }
              }
              resolve();
            },
            error: e => {
              console.log(e);
              generalError = e;
              resolve();
            },
          });
        });
        break;
      case originDataEnumEmailPerAd.personNumber:
        await new Promise<void>(resolve => {
          this.collaboratorsService.GetByPersonNumber(payload).subscribe({
            next: d => {
              const t = d.data;
              if (t) {
                userData = t;
              }
              resolve();
            },
            error: e => {
              generalError = e;
              console.log(e);
              resolve();
            },
          });
        });
        break;
      case originDataEnumEmailPerAd.curp:
        await new Promise<void>(resolve =>
          this.collaboratorsService.GetByCurp(payload).subscribe({
            next: d => {
              const t = d.data;
              if (t) {
                userData = t;
              }
              resolve();
            },
            error: e => {
              generalError = e;
              console.log(e);
              resolve();
            },
          })
        );
        break;
      default:
        throw new Error("Origin Not Implemented");
    }
    if (generalError) {
      const personalizedError =
        generalError.error as ResponseDataInterfaces<null>;
      if (
        generalError.status === 422 &&
        Number(personalizedError.statusCode) === 422
      ) {
        const errorMsg = personalizedError.message;
        throw new Error(errorMsg);
      }
    }

    return userData;
  }
  postAssistanceV2(
    sessionData: {
      fkIdSesion: number;
      numeroSesion: number;
      fkIdOrigen: originDataEnumEmailPerAd;
    },
    userData: ICollaborator
  ): Observable<IPostAssistanceRegister> {
    const payload: IPostAssistanceRegister = {
      numeroPersona: userData.personNumber,
      fkIdOrigen: sessionData.fkIdOrigen,
      fkIdSesion: sessionData.fkIdSesion,
      numeroSesion: sessionData.numeroSesion,
      email: userData.email ? userData.email : "Info not available",
      nombre: userData.name,
      apellidoPaterno: userData?.lastName,
      curp: userData.curp,
    };
    const url = `${this.endPoint}`;
    const options = {
      headers: new HttpHeaders({
        accept: "text/plain",
        "Content-Type": "application/json",
      }),
    };

    return this.http
      .post<ResponseDataInterfaces<IPostAssistanceRegister>>(
        url,
        payload,
        options
      )
      .pipe(map(d => d.data));
  }
  postAssistanceUserNotFoundV2(
    sessionData: {
      fkIdSesion: number;
      numeroSesion: number;
      fkIdOrigen: originDataEnumEmailPerAd;
    },
    userData: Partial<ICollaborator>
  ): Observable<IPostAssistanceRegister> {
    const payload: Partial<IPostAssistanceRegister> = {
      fkIdOrigen: sessionData.fkIdOrigen,
      fkIdSesion: sessionData.fkIdSesion,
      numeroSesion: sessionData.numeroSesion,
      //
      numeroPersona: userData.personNumber ? userData.personNumber : undefined,
      curp: userData.curp ? userData.curp : undefined,
    };
    const url = `${this.endPoint}`;
    return this.http
      .post<ResponseDataInterfaces<IPostAssistanceRegister>>(url, payload)
      .pipe(map(d => d.data));
  }

  deleteAttendance(attendantIds: string) {
    const params = new HttpParams().append("assistanceIds", attendantIds);
    return this.http.delete(`${this.endPoint}`, { params });
  }

  getDuplicateAttendees(
    objectIdExt: string,
    searchParams: IGetDuplicatesParams
  ): Observable<IAttendant[]> {
    let params = new HttpParams();
    for (const key in searchParams) {
      if (Object.hasOwn(searchParams, key)) {
        const value = searchParams[key as keyof IGetDuplicatesParams];
        if (value !== undefined && value !== null && value !== 0) {
          params = params.append(key, value.toString());
        }
      }
    }
    return this.http
      .get<ResponseDataInterfaces<IAttendant[]>>(
        `${this.endPoint}/duplicatedAssistancesByEvent/${objectIdExt}`,
        { params }
      )
      .pipe(map(({ data }) => data));
  }

  getDuplicateDetailByUser(
    objectIdExt: string,
    searchParams: IGetDuplicatesDetailsParams
  ): Observable<IDuplicateAttendanceSession[]> {
    let params = new HttpParams();
    for (const key in searchParams) {
      if (Object.hasOwn(searchParams, key)) {
        const value = searchParams[key as keyof IGetDuplicatesDetailsParams];
        if (value !== undefined && value !== null && value !== 0) {
          params = params.append(key, value.toString());
        }
      }
    }

    return this.http
      .get<ResponseDataInterfaces<IDuplicateAttendanceSession[]>>(
        `${this.endPoint}/duplicatedOldAssistancesByEventUser/${objectIdExt}`,
        {
          params,
        }
      )
      .pipe(map(({ data }) => data));
  }

  updateDuplicateAttendees(duplicatedAttendees: IAttendant[]) {
    return this.http.put<ResponseDataInterfaces<boolean>>(
      `${this.endPoint}/duplicatedAssitances`,
      duplicatedAttendees
    );
  }

  postDuplicateAttendanceToOtherSession(
    originAndDestinationSessions: IPostDuplicateAttendanceBody
  ): Observable<IPostDuplicateAttendanceResponse> {
    return this.http
      .post<ResponseDataInterfaces<IPostDuplicateAttendanceResponse>>(
        `${this.endPoint}/DuplicateToAnotherSession`,
        originAndDestinationSessions
      )
      .pipe(map(({ data }) => data));
  }

  async addGuestAttendee(
    attendance: IAttendanceOriginValuePair,
    config: IAttendanceFlowModalConfig
  ) {
    const { mode = "self" } = config || {};
    const origin = attendance.type;

    let userD: ICollaborator | null = null;

    try {
      userD = await this.getUserInfoHCM(origin, attendance.value);
    } catch (error: any) {
      this.sharedModal.openModal("error-modal", {
        title: "¡Error!",
        body: error.message || "Usuario no válido",
      });
      return;
    }

    const modalRef = this.modalService.open(ModalValidacionComponent, {
      size: "md",
      centered: true,
      backdrop: "static",
    });

    if (userD) {
      const hideName = userD.name;
      const hideLastName = userD.lastName;

      modalRef.componentInstance.mensaje =
        mode === "self"
          ? `¿Este es tu nombre de usuario?\n ${hideName} ${hideLastName}`
          : `¿Deseas agregar la asistencia de?\n ${hideName} ${hideLastName}`;

      modalRef.componentInstance.flagEncontrado = true;
    } else {
      modalRef.componentInstance.mensaje = `No se encontró ${attendance.value}, ¿deseas registrarlo?`;

      modalRef.componentInstance.flagEncontrado = false;
      modalRef.componentInstance.valor = attendance.value;
    }

    modalRef.componentInstance.Cancel.subscribe(() => {
      modalRef.close();
    });

    modalRef.componentInstance.Confirm.subscribe(() => {
      modalRef.close();

      if (userD) {
        this.registerAttendanceUserFound(userD, origin, config);
      } else {
        this.registerAttendanceUserNotFound(attendance.value, origin, config);
      }
    });
  }

  registerAttendanceUserFound(
    user: ICollaborator,
    originID: originDataEnumEmailPerAd,
    config: Omit<IAttendanceFlowModalConfig, "onSuccessNotFound">
  ) {
    this.postAssistanceV2(
      {
        fkIdOrigen: originID,
        fkIdSesion: Number(config.fkIdSesion),
        numeroSesion: Number(config.numeroSesion),
      },
      user
    ).subscribe({
      next: d => {
        config.onSuccessFound(d);
      },
      error: (e: HttpErrorResponse) => {
        let errMSG = unknownErrorOnAttendance;

        if (e.status === 409) {
          errMSG = errorDuplicateAttendant;
        } else if (e.status === 422) {
          errMSG =
            e.error?.message ||
            "Ocurrió un error de validación al registrar la asistencia.";
        }

        this.sharedModal.openModal("error-modal", {
          title: "¡Error!",
          body: errMSG,
        });
      },
    });
  }

  private registerAttendanceUserNotFound(
    userIdentifier: string,
    origin: originDataEnumEmailPerAd,
    config: IAttendanceFlowModalConfig
  ) {
    this.postAssistanceUserNotFoundV2(
      {
        fkIdOrigen: origin,
        fkIdSesion: config.fkIdSesion,
        numeroSesion: config.numeroSesion,
      },
      {
        curp:
          origin === originDataEnumEmailPerAd.curp ? userIdentifier : undefined,
        personNumber:
          origin === originDataEnumEmailPerAd.personNumber
            ? userIdentifier
            : undefined,
      }
    ).subscribe({
      next: () => {
        config.onSuccessNotFound();
      },
      error: (e: HttpErrorResponse) => {
        let errorMessage = unknownErrorOnAttendance;

        if (e.status === 409) {
          errorMessage = errorDuplicateAttendant;
        } else if (e.status === 422) {
          errorMessage =
            e.error?.message ||
            "Ocurrió un error de validación al registrar la asistencia.";
        }

        this.sharedModal.openModal("error-modal", {
          title: "¡Error!",
          body: errorMessage,
        });
      },
    });
  }
}
